import {useState, useCallback, useMemo} from 'react';
import {SelectedEvent} from '../utils/types';
import {TEvent} from '../../../../../redux/event/event.type';

interface UseEventSelectionProps {
    activeEvents: TEvent[];
}

export function useEventSelection({activeEvents}: UseEventSelectionProps) {
    const [selectedEvents, setSelectedEvents] = useState<SelectedEvent[]>([]);

    // Calculate statistics
    const selectedCount = selectedEvents.length;
    const mandatoryCount = selectedEvents.filter(se => se.isMandatory).length;
    const optionalCount = selectedCount - mandatoryCount;

    // Check if all active events are selected
    const allActiveSelected = useMemo(() => {
        if(activeEvents.length === 0) return false;
        const selectedIds = new Set(selectedEvents.map(se => se.eid));
        return activeEvents.every(event => selectedIds.has(event.eid));
    }, [activeEvents, selectedEvents]);

    const handleEventSelect = useCallback((eid: number, isSelected: boolean) => {
        setSelectedEvents(prev => {
            if(isSelected) {
                // Check if event is already selected to avoid duplicates
                if(prev.some(se => se.eid === eid)) return prev;
                return [...prev, {eid, isMandatory: false}];
            } else {
                // Check if the event exists before filtering
                if(!prev.some(se => se.eid === eid)) return prev;
                return prev.filter(se => se.eid !== eid);
            }
        });
    }, []);

    const handleEventKindChange = useCallback((eid: number, isMandatory: boolean) => {
        setSelectedEvents(prev => 
            prev.map(se => se.eid === eid ? {...se, isMandatory} : se)
        );
    }, []);

    const handleSelectAllActive = useCallback((isSelected: boolean) => {
        if(isSelected) {
            setSelectedEvents(prev => {
                const existingIds = new Set(prev.map(se => se.eid));
                const newSelections = activeEvents
                    .filter(event => !existingIds.has(event.eid))
                    .map(event => ({eid: event.eid, isMandatory: false}));
                return newSelections.length > 0 ? [...prev, ...newSelections] : prev;
            });
        } else {
            setSelectedEvents(prev => {
                const activeIds = new Set(activeEvents.map(e => e.eid));
                const filtered = prev.filter(se => !activeIds.has(se.eid));
                return filtered.length !== prev.length ? filtered : prev;
            });
        }
    }, [activeEvents]);

    const handleClearAll = useCallback(() => {
        setSelectedEvents([]);
    }, []);

    return {
        selectedEvents,
        selectedCount,
        mandatoryCount,
        optionalCount,
        allActiveSelected,
        handleEventSelect,
        handleEventKindChange,
        handleSelectAllActive,
        handleClearAll
    };
}

